<template>
  <div class="soar-container">
    <template v-if="!loading">
      <div v-if="publicServicesStore.selectedCategory" class="flex flex-col gap-y-4">
        <div class="flex flex-col gap-y-2">
          <h1 class="text-3xl">{{ publicServicesStore.selectedCategory.publicCategoryDesc }}</h1>
          <p v-if="$i18n.locale == 'en'" class="text-gray-500 text-justify">
            {{ publicServicesStore.selectedCategory.categoryConfig.englishDescription }}
          </p>
          <p v-if="$i18n.locale == 'ar'" class="text-gray-500 text-justify">
            {{ publicServicesStore.selectedCategory.categoryConfig.arabicDescription }}
          </p>
        </div>
        <div class="flex justify-between flex-col md:flex-row">
          <h2 class="text-2xl text-primary">{{ $t('availableServices') }}</h2>
          <div class="w-full md:w-3/6 flex gap-x-2">
            <div class="flex-1 flex justify-center items-center">
              <div class="w-full relative border border-brand-grayblue-300 h-full flex rounded-md">
                <input
                  :placeholder="$t('searchFor')"
                  type="text"
                  class="w-full py-1 ps-3 rounded-s-md bg-white rounded-md placeholder:text-xs"
                  v-model="searchValue"
                />
                <div
                  class="absolute top-0 end-0 h-full aspect-square bg-brand-gray-300 rounded-e-md p-2.5"
                >
                  <faMagnifyingGlass class="" />
                </div>
              </div>
            </div>
            <button
              class="rounded-md bg-gray-400/80 hover:bg-gray-400 px-3 py-1.5"
              @click="$router.back()"
            >
              <div class="w-full flex items-center gap-x-2">
                <faChevronLeft class="w-2 fill-white" />
                <span class="ltr:font-sans tracking-wide flex-1 text-white text-sm pb-[1px]">
                  {{ $t('Back') }}
                </span>
              </div>
            </button>
          </div>
        </div>
      </div>
      <div :key="$i18n.locale" class="pt-4 flex flex-wrap">
        <div class="w-full h-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <template :key="key" v-for="(service, key) in filteredServices">
            <ServiceCardVue
              :label="service.localizedServiceName"
              :serviceId="service.transactionId"
              :service-slug="service.subCategoryServiceConfig.key"
              category="public"
            >
              <template #start-service>
                <router-link
                  v-if="service.transactionId"
                  :to="{
                    name: 'ServiceForm',
                    params: {
                      category: 'public',
                      serviceId: service.transactionId
                    }
                  }"
                  class="w-fit ps-1 rounded-md bg-primary/80 hover:bg-primary flex items-center justify-between hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed font-normal"
                >
                  <span class="ps-2 pe-1 ltr:font-sans tracking-wide text-white text-xs pb-[1px]">
                    {{ $t('serviceStart') }}
                  </span>
                  <div class="flex justify-center items-center h-full bg-black/10 p-1.5 rounded-e">
                    <FontAwesomeIcon
                      :icon="faArrowCircleRight"
                      class="w-4 fill-primary/80 hover:fill-primary text-white"
                    />
                  </div>
                </router-link>
                <template v-else>
                  <router-link
                    :to="{
                      name: 'AddlServiceForm',
                      params: {
                        category: 'public',
                        serviceSlug: service.subCategoryServiceConfig.key
                      }
                    }"
                    class="w-fit ps-1 rounded-md bg-primary/80 hover:bg-primary flex items-center justify-between hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed font-normal"
                  >
                    <span class="ps-2 pe-1 ltr:font-sans tracking-wide text-white text-xs pb-[1px]">
                      {{ $t('serviceStart') }}
                    </span>
                    <div
                      class="flex justify-center items-center h-full bg-black/10 p-1.5 rounded-e"
                    >
                      <FontAwesomeIcon
                        :icon="faArrowCircleRight"
                        class="w-4 fill-primary/80 hover:fill-primary text-white"
                      />
                    </div>
                  </router-link>
                </template>
              </template>
              <template #applicationTracking>
                <RouterLink
                  :to="{ name: 'PublicVisaApplicationTracking' }"
                  class="w-fit px-2 rounded-md bg-primary/80 hover:bg-primary flex items-center justify-between hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed font-normal"
                >
                  <span class="ltr:font-sans tracking-wide text-white text-xs pb-[1px]">
                    {{ $t('applicationTrackingMenu') }}
                  </span>
                </RouterLink>
              </template>
            </ServiceCardVue>
          </template>
        </div>
      </div>
    </template>
    <div v-else class="pt-4 flex flex-wrap">
      <div class="w-full h-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <template :key="key" v-for="(i, key) in [1, 3, 2, 4, 1, 2, 3, 4]">
          <div
            class="p-4 max-h-max flex flex-col gap-y-6 justify-center items-center border border-gray-200 bg-gray-100 animate-pulse rounded-xl aspect-video hover:scale-105"
          ></div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import faChevronLeft from '@/assets/icons/faChevronLeft.svg?component'
import faMagnifyingGlass from '@/assets/icons/faMagnifyingGlass.svg'
import ServiceCardVue from '@/components/cards/ServiceCard.vue'
import { useGlobalStore } from '@/stores/global'
import { usePublicServicesStore } from '@/stores/public_services'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { faArrowCircleRight } from '@fortawesome/free-solid-svg-icons'
import Fuse from 'fuse.js'

const i18n = useI18n()
const route = useRoute()
const router = useRouter()
const globalStore = useGlobalStore()
const publicServicesStore = usePublicServicesStore()

const fuse = ref()
const searchValue = ref()
const loading = ref(true)
const services = ref([])

const languageCode = computed(() => (i18n.locale.value == 'en' ? 2 : 1))

watch(useI18n().locale, async () => {
  await globalStore.getServicesList()
  await nextTick()
  router.replace({ path: '/services/public' })
})

const filteredServices = computed(() => {
  if (!searchValue.value?.trim()) {
    return services.value
  }

  const results = fuse.value.search(searchValue.value)
  return results.map((r) => r.item)
})

onMounted(async () => {
  await publicServicesStore.getCategories({ languageCode: languageCode.value })
  await nextTick()

  const category = publicServicesStore.categories.find((c) => c.slug == route.params.category)
  publicServicesStore.setCategory(category)

  services.value = await publicServicesStore.getServicesForCategory()

  fuse.value = new Fuse(
    services.value.map((s) => ({ ...s, serviceTransIdAsString: `${s.serviceTransId}` })),
    {
      threshold: 0.5,
      keys: ['localizedServiceName', 'serviceTransIdAsString']
    }
  )

  searchValue.value = route.query.search
  loading.value = false
  window.scrollTo(0, 0)
})
</script>

<script>
export default { name: 'ServicesGrid' }
</script>

<style lang="scss" scoped></style>
